import { PauseOptions, StreamInfoPayload } from ".";
import {
  AWPAudioTrack,
  AWPTextTrack,
  AWPTextTrackSettings,
} from "./models/track-models";
import { PlayerMode } from "./models/visuals";

/**
 * Utility type to ensure all keys in are defined, without forcing the values to be
 * This converts props from ex { propName? string; } to { propName: string | undefined; }
 */
type NonPartial<T> = { [K in keyof Required<T>]: T[K] };

export enum PlaybackState {
  IDLE = "idle",
  LOADING = "loading",
  PLAYING = "playing",
  PAUSED = "paused",
  SEEKING = "seeking",
  BUFFERING = "buffering",
  ENDED = "ended",
}

export enum PlaybackMode {
  DEFAULT = "default",
  LIVE_DAI = "liveDai",
  ORIGIN = "origin",
  ORIGIN_FALLBACK = "origin_fallback",
  START_OVER = "startOver",
}

// Uses NonPartial to ensure all properties are defined in DEFAULT_PLAYER_STATE, so they will be reset when calling state reset().
export type PlayerState = NonPartial<{
  playerMode: PlayerMode;
  isLoaded: boolean;
  playbackState: PlaybackState;
  canSeek: boolean;
  canPause: boolean;
  canGoToLive: boolean;
  canStartOver: boolean;
  isAd: boolean;
  isPauseAd: boolean;
  isLive: boolean;
  playbackMode: PlaybackMode;
  isFullscreen: boolean;
  isCasting: boolean;
  autoplayBlocked: boolean;
  muted: boolean;
  volume: number;
  preSeeking: boolean;
  currentTime: number;
  utcCurrentTimeMs?: number;
  utcStartTimeMs?: number;
  duration: number;
  streamInfo?: StreamInfoPayload;
  textTracks: AWPTextTrack[];
  activeTextTrack: AWPTextTrack | null;
  textTrackSettings: AWPTextTrackSettings;
  audioTracks: AWPAudioTrack[];
  activeAudioTrack: AWPAudioTrack | null;
  currentBitrate: number;
  debugOverlay: boolean;
  playbackRate: number;
}>;

export const DEFAULT_PLAYER_STATE: PlayerState = {
  isLoaded: false,
  playbackState: PlaybackState.IDLE,
  canSeek: false,
  canPause: false,
  canGoToLive: false,
  canStartOver: false,
  isAd: false,
  isPauseAd: false,
  isLive: false,
  playbackMode: PlaybackMode.DEFAULT,
  isFullscreen: false,
  isCasting: false,
  autoplayBlocked: false,
  muted: false,
  volume: 1,
  preSeeking: false,
  currentTime: 0,
  utcCurrentTimeMs: undefined,
  utcStartTimeMs: undefined,
  duration: 0,
  textTracks: [],
  activeTextTrack: null,
  textTrackSettings: {},
  audioTracks: [],
  activeAudioTrack: null,
  streamInfo: undefined,
  currentBitrate: 0,
  debugOverlay: false,
  playbackRate: 1,
  playerMode: PlayerMode.DEFAULT,
};

export type TControls = {
  play?: () => void;
  pause?: (options: PauseOptions) => void;
  mute?: () => void;
  unmute?: () => void;
  toggleMute?: () => void;
  setVolume?: (volume: number) => void;
  preSeek?: (amount: number) => void;
  applyPreSeek?: () => void;
  seekForward?: (amount: number) => void;
  seekBackward?: (amount: number) => void;
  seekTo?: (position: number, debounce?: boolean) => void;
  seekToLive?: () => void;
  seekToStartOver?: () => void;
  setAudioTrack?: (track: AWPAudioTrack) => void;
  setTextTrack?: (track: AWPTextTrack) => void;
  setSubtitleText?: (text: string) => void;
  setSubtitleTextSize?: (size: number) => void;
  toggleFullscreen?: () => void;
  enterFullscreen?: () => void;
  exitFullscreen?: () => void;
  toggleAirplay?: () => void;
  setPlaybackRate: (num: number) => void;
} | null;
